/// 
// MainForm is a functional component in React that is a form with multiple steps.
// The form has four steps: "Problem Details", "Appointment Details", "Booking", and "Complete Form". 
// The getStepContent function is used to determine which step the user is currently on and render the corresponding form for that step.
// The submit function is used to submit the form, and the updateJob function updates the job with notes related to the form. 
// The MainForm component accepts a parent prop and uses it to pass down to the form components for each step. 
// It also uses various hooks from the useAppSelector hook to get state values from the global store and the dispatchUILoading and dispatchUILoaded functions to update the UI loading state.
///
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { state, ui } from '../../../../interfaces/interfaces';
import FormBar from './FormBar';
import Box from '@mui/material/Box';
import Copyright from './Footer';
import useDebugInformation from "@hooks/useDebugInformation";
import StepThreeForm from './Steps/StepThree';
import StepTwoForm from './Steps/StepTwo';
import StepOneForm from './Steps/StepOne';
import CompleteForm from './Steps/CompleteForm';
import ContactUtils from "@utils/contact";
import { Grid2 as Grid } from '@mui/material';
import { useAppSelector } from "@store/hooks";
import ErrorButton from './ErrorButton';
import job from "@utils/objects/job";
import { dispatchUILoading, dispatchUILoaded } from "@reducers/uiReducer";

const name = "MainForm"

const steps = ['Problem Details', 'Appointment Details', 'Booking'];

const getStepContent = (step: number, parent: string) => {
    switch (step) {
        case 0:
            return <StepOneForm parent={parent} />;
        case 1:
            return <StepTwoForm parent={parent} />;
        case 2:
            return <StepThreeForm parent={parent} />;
        default:
            throw new Error('Unknown step');
    }
}

const submit = async () => {
    state.SavingJob = true;
    dispatchUILoading();
    const result = await job.submit();

    if (result) {
        ui.ProblemMsg = "Booking saved.";
        ui.ProblemMsgVariant = "success";
        ui.ProblemShowMsg = true;
        state.SavingJob = false;
    }
    else {
        ui.ProblemMsg = "Error submitting job"
        ui.ProblemMsgVariant = "error"
        ui.ProblemShowMsg = true
        state.JobStatusId = 99;
        state.SavingJob = false;
    }
    dispatchUILoaded()
}

const updateJob = async () => {
    const isLandlord = JSON.parse(ui.ProblemTabsList).find((tab: { label: string; }) => tab.label === "Landlord")?.label === "Landlord"
    const isRelated = JSON.parse(ui.ProblemTabsList).find((tab: { label: string; }) => tab.label === "Related")?.label === "Related"
    if (isLandlord) {
        state.NotesLandlord = (ContactUtils.convertContactFormFieldsToContact({ scope: "LandlordContact", ui }))?.contactString + ""
    }
    if (!isLandlord) {
        state.NotesLandlord = ""
    }
    if (isRelated) {
        state.NotesRelated = (ContactUtils.convertContactFormFieldsToContact({ scope: "RelatedContact", ui }))?.contactString + "\nRelationship: " + ui.RelatedContactRelationship
    }
    if (!isRelated) {
        state.NotesRelated = ""
    }

    const caller = ui.CustomerType.charAt(0).toUpperCase() + ui.CustomerType.slice(1);
    state.NotesCaller = caller
}

type Props = {
    parent: string
}


const MainForm: React.FC<Props> = ({ parent }) => {
    const activeStep = useAppSelector(({ ui }) => ui.ActiveStep)
    const last2 = useAppSelector(({ temp }) => temp.Last2)
    const stage1Complete = useAppSelector(({ ui }) => ui.Stage1Complete)
    const stage2Complete = useAppSelector(({ ui }) => ui.Stage2Complete)
    const stage3Complete = useAppSelector(({ ui }) => ui.Stage3Complete)
    const rejectionReason = useAppSelector(({ ui }) => ui.BookingRejectionReason)
    const propertyAddressModalOpen = useAppSelector(({ ui }) => ui.PropertyAddressModalOpen)
    const isSavingJob = useAppSelector(({ state }) => state.SavingJob)

    const debugName = `${parent}▷[${name}]`

    useEffect(() => {
        if (last2?.length > 0) {
            ui.ProblemMsg = "History related to phone number found!"
            ui.ProblemMsgVariant = "success"
            ui.ProblemShowMsg = true;
        }
    }, [last2])

    useEffect(() => {
        const saveOnStepChange = async () => {
            await job.saveIndexedDBOnExit({ page: "BookingForm", action: "activeStep", message: "Booking saved", reason: "Page change", booking: state.JobId })
        }
        saveOnStepChange()
    }, [activeStep])

    useDebugInformation(debugName, { last2, activeStep, stage1Complete, stage2Complete, stage3Complete })
    return (
        (<Box id="form-panel" sx={{ width: 640 }}>
            <FormBar />
            <Paper sx={(theme) => ({
                padding: theme.spacing(1),
                [theme.breakpoints.up(600 + Number(theme.spacing(3)) * 2)]: {
                    padding: theme.spacing(1),
                },
            })}>
                <Typography component="h6" variant="h6" align="center">
                    {activeStep === 0 ? " Problem Details" : activeStep === 1 ? "Appointment Details" : "Booking"}
                </Typography>
                <Stepper activeStep={activeStep} sx={(theme) => ({
                    padding: theme.spacing(1),
                })}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <>
                    {activeStep === steps.length ? (
                        <CompleteForm parent={debugName} />
                    ) : (
                        <>
                            <Paper variant="elevation" elevation={4} sx={(theme) => ({
                                padding: theme.spacing(1),
                                [theme.breakpoints.up(600 + Number(theme.spacing(2)) * 2)]: {
                                    padding: theme.spacing(1),
                                },
                                minHeight: 580
                            })}>
                                {getStepContent(activeStep, debugName)}</Paper>
                            <Grid container >
                                <Grid size={6}>
                                    {activeStep === 0 && !propertyAddressModalOpen && <ErrorButton />}
                                </Grid>
                                <Grid size={6}>

                                    <Box display="flex" justifyContent="flex-end">
                                        {activeStep !== 0 && (
                                            <Button onClick={() => ui.ActiveStep = (activeStep - 1) as 0 | 1 | 2 | 3} sx={{ mt: 1, ml: 1 }}>
                                                Back
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={(activeStep === 0 && !stage1Complete) || (activeStep === 1 && !stage2Complete) || (activeStep === 2 && !stage3Complete) || isSavingJob}
                                            onClick={() => {
                                                if (activeStep === 0 || activeStep === 1) {
                                                    updateJob()
                                                }
                                                if (activeStep === steps.length - 1 && (!rejectionReason || !rejectionReason?.failedId))
                                                    ui.FinalDialogModal = true
                                                else if (activeStep === steps.length - 1 && rejectionReason?.failedId) {
                                                    state.SavingJob = true;
                                                    submit()
                                                    ui.ActiveStep = (activeStep + 1) as 0 | 1 | 2 | 3;
                                                }
                                                else {
                                                    ui.ActiveStep = (activeStep + 1) as 0 | 1 | 2 | 3;
                                                }
                                            }
                                            }
                                            sx={{ mt: 1, ml: 1 }}
                                        >
                                            {activeStep === steps.length - 1 ? 'Save booking' : 'Next'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </>
            </Paper>
            <Copyright />
        </Box >)
    );
}

MainForm.displayName = name;


export default React.memo(MainForm)